import React, { useState } from 'react';
import { Input } from './Input';
import TimePicker24Hrs from './TimePicker24Hrs';
import { DatePickerWithInput } from './DatePickerWithInput';

// Extract just the date part from a datetime format pattern
const extractDatePattern = (dateTimeFormat: string): string => {
  // Remove time components for the date input
  return dateTimeFormat
    .replace(/\s*HH.*$/, '') // Remove HH:mm:ss and everything after
    .replace(/\s*hh.*$/, '') // Remove hh:mm:ss and everything after
    .trim();
};

interface DateTimeValue {
  date: Date;
  dateString: string;
  timeString: string;
}

interface DatetimePickerWithInputProps {
  value?: DateTimeValue;
  onChange?: (value: DateTimeValue) => void;
  dateTimeFormat?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function DatetimePickerWithInput({
  value,
  onChange,
  dateTimeFormat = 'yyyy-MM-dd HH:mm:ss',
  placeholder,
  className = '',
  disabled = false,
}: DatetimePickerWithInputProps) {
  const dateOnlyPattern = extractDatePattern(dateTimeFormat);

  const [timeValue, setTimeValue] = useState<string>(() => {
    if (!value?.date) return '10:30:00';
    const hours = value.date.getHours().toString().padStart(2, '0');
    const minutes = value.date.getMinutes().toString().padStart(2, '0');
    const seconds = value.date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  });

  const combineDateTime = (date: Date, time: string): Date => {
    const [hours, minutes, seconds = '00'] = time.split(':');
    const combined = new Date(date);
    combined.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds));
    return combined;
  };

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    if (dateOnlyPattern.includes('yyyy-MM-dd')) {
      return `${year}-${month}-${day}`;
    } else if (dateOnlyPattern.includes('MM/dd/yyyy')) {
      return `${month}/${day}/${year}`;
    } else if (dateOnlyPattern.includes('dd/MM/yyyy')) {
      return `${day}/${month}/${year}`;
    }
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const combined = combineDateTime(selectedDate, timeValue);
      const dateString = formatDateString(selectedDate);

      onChange?.({
        date: combined,
        dateString: dateString,
        timeString: timeValue,
      });
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setTimeValue(newTime);

    if (value?.date) {
      const combined = combineDateTime(value.date, newTime);
      onChange?.({
        date: combined,
        dateString: value.dateString,
        timeString: newTime,
      });
    }
  };

  return (
    <div className={`flex gap-4 ${className}`}>
      <div className="flex flex-col gap-3 flex-1">
        <DatePickerWithInput
          value={value?.date}
          onChange={handleDateChange}
          dateFormat={dateOnlyPattern}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>

      <div className="flex flex-col gap-3">
        <TimePicker24Hrs
          value={timeValue}
          onChange={handleTimeChange}
          className="bg-background w-36"
        />
      </div>
    </div>
  );
}
